import { apiClient } from '@/utils/api-client';
import type { ApiResponse, JsonApiResource } from '@/types/api';

export interface TerminologyRule {
  id: string;
  rule_type: 'trademark' | 'forbidden' | 'approved_term' | 'wrong_correct';
  severity: 'critical' | 'major' | 'minor';
  category: string | null;
  pattern: string;
  pattern_type: 'exact' | 'regex' | 'word_boundary';
  case_sensitive: boolean;
  replacement: string | null;
  replacement_first_use: string | null;
  replacement_subsequent: string | null;
  usage_notes: string | null;
  is_active: boolean;
  sort_order: number;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface TerminologyRuleCreateData {
  rule_type: string;
  severity: string;
  category?: string;
  pattern: string;
  pattern_type: string;
  case_sensitive?: boolean;
  replacement?: string;
  replacement_first_use?: string;
  replacement_subsequent?: string;
  usage_notes?: string;
  is_active?: boolean;
  sort_order?: number;
}

export interface TerminologyRuleUpdateData {
  rule_type?: string;
  severity?: string;
  category?: string;
  pattern?: string;
  pattern_type?: string;
  case_sensitive?: boolean;
  replacement?: string;
  replacement_first_use?: string;
  replacement_subsequent?: string;
  usage_notes?: string;
  is_active?: boolean;
  sort_order?: number;
}

export interface TerminologyListParams {
  active?: string;
  category?: string;
  rule_type?: string;
  severity?: string;
  q?: string;
}

export interface TerminologyStats {
  total: number;
  active: number;
  categories: string[];
  by_severity: { critical: number; major: number; minor: number };
  by_rule_type: { trademark: number; forbidden: number; approved_term: number; wrong_correct: number };
}

export const terminologyService = {
  async list(params?: TerminologyListParams): Promise<ApiResponse<JsonApiResource<TerminologyRule>[]>> {
    const queryParams: Record<string, string> = {};
    if (params?.active) queryParams.active = params.active;
    if (params?.category) queryParams.category = params.category;
    if (params?.rule_type) queryParams.rule_type = params.rule_type;
    if (params?.severity) queryParams.severity = params.severity;
    if (params?.q) queryParams.q = params.q;
    return apiClient.get('/api/admin/terminology_rules', queryParams);
  },

  async get(id: string): Promise<ApiResponse<JsonApiResource<TerminologyRule>>> {
    return apiClient.get(`/api/admin/terminology_rules/${id}`);
  },

  async create(data: TerminologyRuleCreateData): Promise<ApiResponse<JsonApiResource<TerminologyRule>>> {
    return apiClient.post('/api/admin/terminology_rules', { terminology_rule: data });
  },

  async update(id: string, data: TerminologyRuleUpdateData): Promise<ApiResponse<JsonApiResource<TerminologyRule>>> {
    return apiClient.patch(`/api/admin/terminology_rules/${id}`, { terminology_rule: data });
  },

  async destroy(id: string): Promise<void> {
    return apiClient.delete(`/api/admin/terminology_rules/${id}`);
  },

  async stats(): Promise<TerminologyStats> {
    return apiClient.get('/api/admin/terminology_rules/stats');
  },
};
